import React, { memo, useMemo } from 'react'
import { Card, Button, Input, Table, Spin, Avatar, Select, Tag, Tooltip, Popover } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import moment from 'moment'
import './CreditUsageTable.scss'

const { Search } = Input
const { Option } = Select

const CreditUsageTable = memo(({
  title = 'Credit Usage Report',
  data,
  loading,
  onExportClick,
  onSearch,
  searchTerm,
  pagination,
  onPageChange,
  activeTypes = [],
  selectedType,
  onTypeChange
}) => {
  const formatModelName = (modelString) => {
    if (!modelString) return ''
    return modelString
      .replace(/[-_]/g, ' ')
      .replace(/\bpreview\b/gi, '')
      .replace(/\blatest\b/gi, '')
      .replace(/\bstable\b/gi, '')
      .trim()
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const tableData = useMemo(() => {
    if (!data) return []

    return data.map((item, index) => {
      const creator = item.creator || item.user;
      const creatorName = `${creator?.first_name || ''} ${creator?.last_name || ''}`.trim() || item.username || ""

      const creatorAvatar = creator?.avatar?.link || creator?.avatar || ''

      const commonData = {
        ...item,
        key: item.sk || item.pk || index,
        creatorName,
        creatorAvatar,
        timestamp: item.timestamp || item.created_at || '-',
        totalCost: item.total_cost || 0,
        totalCredits: item.total_credits || 0,
        organization: item.organization || item.org_id || '-',
      }

      if (selectedType === 'sharebot') {
        return {
          ...commonData,
          question: item.question || '-',
        }
      }

      if (selectedType === 'ai-image' || selectedType === 'ai_video') {
        return {
          ...commonData,
          prompt: item.user_prompt || '-',
          model: item.model || '-',
          voiceover_text: item.voiceover_text || '-',
          duration: item.duration || 0,
        }
      }

      if (selectedType === 'audio') {
        return {
          ...commonData,
          model: item.model || '-',
          text: item.text || '-',
          provider: item.provider || '-',
        }
      }

      if (selectedType === 'podcast') {
        return {
          ...commonData,
          title: item.title || '-',
          duration: item.duration || 0,
        }
      }

      if (selectedType === 'maze') {
        return {
          ...commonData,
          title: item.title || '-',
          depth: item.depth || 0,
        }
      }

      // Default/Course type
      return {
        ...commonData,
        key: item.course_id || index,
        courseName: item.title || 'Unknown Course',
        totalPages: item.total_pages || 0,
        maxPictures: item.max_picture_allowed || 0,
        actualPhotos: item.actual_photos || 0,
        extraPhotos: item.extra_photos || 0,
        extraCostPhotos: item.extra_cost_for_photos || 0,
        maxTextsPerPage: item.max_texts_per_page || 0,
        maxTextsAllowed: item.max_texts_allowed || 0,
        actualText: item.actual_texts || 0,
        extraText: item.extra_texts || 0,
        extraCostText: item.extra_cost_for_texts || 0,
        maxVoicesPerPage: item.max_voices_per_page || 0,
        maxVoicesAllowed: item.max_voices_allowed || 0,
        actualVoiceSecs: item.actual_voices || 0,
        extraVoiceSecs: item.extra_voices || 0,
        extraCostVoice: item.extra_cost_for_voices || 0,
        basicCostToken: item.total_basic_cost_token || 0,
        totalCostTokens: item.total_tokens_cost || 0,
      }
    })
  }, [data, selectedType])

  const columns = useMemo(() => {
    const creatorColumn = {
      title: 'Creator',
      dataIndex: 'creatorName',
      key: 'creatorName',
      width: 200,
      render: (name, record) => (
        <div className="user-info" style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar src={record.creatorAvatar} className="user-avatar" style={{ marginRight: '8px' }}>
            {name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <span className="user-name" style={{ fontWeight: 500 }}>{name}</span>
        </div>
      )
    }

    if (selectedType === 'course') {
      return [
        creatorColumn,
        {
          title: 'Course Name',
          dataIndex: 'courseName',
          key: 'courseName',
          width: 150,
        },
        {
          title: 'Total Pages',
          dataIndex: 'totalPages',
          key: 'totalPages',
          width: 100,
        },
        {
          title: 'Max Pictures (Pages x 2)',
          dataIndex: 'maxPictures',
          key: 'maxPictures',
          width: 150,
        },
        {
          title: 'Actual Photos',
          dataIndex: 'actualPhotos',
          key: 'actualPhotos',
          width: 120,
        },
        {
          title: 'Extra Photos',
          dataIndex: 'extraPhotos',
          key: 'extraPhotos',
          width: 120,
        },
        {
          title: 'Extra Cost (Photos)',
          dataIndex: 'extraCostPhotos',
          key: 'extraCostPhotos',
          width: 120,
          render: (val) => Number(val).toLocaleString(undefined, { maximumFractionDigits: 6 })
        },
        {
          title: 'Max Texts Per Page',
          dataIndex: 'maxTextsPerPage',
          key: 'maxTextsPerPage',
          width: 150,
        },
        {
          title: 'Max Texts Allowed',
          dataIndex: 'maxTextsAllowed',
          key: 'maxTextsAllowed',
          width: 150,
        },
        {
          title: 'Actual Text',
          dataIndex: 'actualText',
          key: 'actualText',
          width: 100,
        },
        {
          title: 'Extra Text',
          dataIndex: 'extraText',
          key: 'extraText',
          width: 100,
        },
        {
          title: 'Extra Cost (Text)',
          dataIndex: 'extraCostText',
          key: 'extraCostText',
          width: 120,
          render: (val) => Number(val).toLocaleString(undefined, { maximumFractionDigits: 6 })
        },
        {
          title: 'Max Voices Per Page',
          dataIndex: 'maxVoicesPerPage',
          key: 'maxVoicesPerPage',
          width: 150,
        },
        {
          title: 'Max Voices Allowed (Sec)',
          dataIndex: 'maxVoicesAllowed',
          key: 'maxVoicesAllowed',
          width: 150,
        },
        {
          title: 'Actual Voice (Sec)',
          dataIndex: 'actualVoiceSecs',
          key: 'actualVoiceSecs',
          width: 120,
          render: (val) => Number(val).toFixed(2)
        },
        {
          title: 'Extra Voice (Sec)',
          dataIndex: 'extraVoiceSecs',
          key: 'extraVoiceSecs',
          width: 120,
          render: (val) => Number(val).toFixed(2)
        },
        {
          title: 'Extra Cost (Voice)',
          dataIndex: 'extraCostVoice',
          key: 'extraCostVoice',
          width: 120,
          render: (val) => Number(val).toLocaleString(undefined, { maximumFractionDigits: 6 })
        },
        {
          title: 'Basic Cost Credit (Pages x 60)',
          dataIndex: 'basicCostToken',
          key: 'basicCostToken',
          width: 150,
          render: (val) => Number(val).toLocaleString(undefined, { maximumFractionDigits: 6 })
        },
        {
          title: 'Total Cost Credit',
          dataIndex: 'totalCostTokens',
          key: 'totalCostTokens',
          width: 120,
          render: (val) => Number(val).toLocaleString(undefined, { maximumFractionDigits: 6 })
        },
        {
          title: 'Usage Date',
          dataIndex: 'timestamp',
          key: 'timestamp',
          width: 120,
          render: (val, record) => {
            const date = val && val !== '-' ? val : record.created_at
            return date && date !== '-' ? moment(date).format('YYYY-MM-DD HH:mm:ss') : '-'
          }
        }
      ]
    }

    const cols = []

    // 1. Creator
    cols.push(creatorColumn)

    // 2. Service Detail (Title/Model/Question)
    if (selectedType === 'sharebot') {
      cols.push({
        title: 'Question',
        dataIndex: 'question',
        key: 'question',
        width: 300,
        render: (text) => (
          <Tooltip title={text} placement="topLeft">
            <span style={{ display: 'inline-block', maxWidth: '280px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {text}
            </span>
          </Tooltip>
        )
      })
    } else if (selectedType === 'ai-image' || selectedType === 'ai_video') {
      /*
      cols.push({
        title: 'Prompt',
        dataIndex: 'prompt',
        key: 'prompt',
        width: 250,
        render: (text) => (
          <Tooltip title={text} placement="topLeft">
            <span style={{ display: 'inline-block', maxWidth: '230px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {text}
            </span>
          </Tooltip>
        )
      })
      */
      cols.push({
        title: selectedType === 'ai-image' ? 'User Prompt' : 'Voiceover Text',
        dataIndex: selectedType === 'ai-image' ? 'user_prompt' : 'voiceover_text',
        key: selectedType === 'ai-image' ? 'user_prompt' : 'voiceover_text',
        width: 150,
        render: (voiceover_text) => (
          <Popover
            content={
              <div style={{
                maxWidth: '450px',
                maxHeight: '350px',
                overflowY: 'auto',
                padding: '12px',
                fontSize: '14px',
                lineHeight: '1.6',
                color: 'rgba(0, 0, 0, 0.85)',
                wordBreak: 'break-word'
              }}>
                {voiceover_text}
              </div>
            }
            title={<span style={{ fontWeight: 600 }}>{selectedType === 'ai-image' ? 'User Prompt' : 'Voiceover Text'}</span>}
            trigger="hover"
            placement="topLeft"
            overlayStyle={{ padding: 0 }}
          >
            <Tag
              color="geekblue"
              style={{
                borderRadius: '4px',
                border: 'none',
                background: '#f0f5ff',
                padding: '2px 8px',
                fontSize: '13px',
                margin: 0,
                maxWidth: '130px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
                cursor: 'help'
              }}
            >
              {voiceover_text}
            </Tag>
          </Popover>
        )
      })
    } else if (selectedType === 'audio') {
      cols.push({
        title: 'Provider',
        dataIndex: 'provider',
        key: 'provider',
        width: 150,
        render: (text) => (
          <span style={{ textTransform: 'capitalize' }}>{text}</span>
        )
      })
      cols.push({
        title: 'Model Used',
        dataIndex: 'model',
        key: 'model',
        render: (_, record) => {
          return record.model ? (
            <Tag color="geekblue" style={{ borderRadius: '4px', border: 'none', background: '#f0f5ff', padding: '2px 8px', fontSize: '13px', margin: 0 }} title={record.model}>
              {formatModelName(record.model)}
            </Tag>
          ) : <span style={{ color: '#aaa' }}>-</span>
        }
      })
      cols.push({
        title: 'Text',
        dataIndex: 'text',
        key: 'text',
        width: 250,
        render: (text) => (
          <Popover
            content={
              <div style={{
                maxWidth: '450px',
                maxHeight: '350px',
                overflowY: 'auto',
                padding: '12px',
                fontSize: '14px',
                lineHeight: '1.6',
                color: 'rgba(0, 0, 0, 0.85)',
                wordBreak: 'break-word'
              }}>
                {text}
              </div>
            }
            title={<span style={{ fontWeight: 600 }}>Text</span>}
            trigger="hover"
            placement="topLeft"
            overlayStyle={{ padding: 0 }}
          >
            <Tag
              color="geekblue"
              style={{
                borderRadius: '4px',
                border: 'none',
                background: '#f0f5ff',
                padding: '2px 8px',
                fontSize: '13px',
                margin: 0,
                maxWidth: '230px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
                cursor: 'help'
              }}
            >
              {text}
            </Tag>
          </Popover>
        )
      })
    } else if (selectedType === 'podcast') {
      cols.push({
        title: 'Podcast Name',
        dataIndex: 'title',
        key: 'title',
        width: 250,
        render: (title) => (
          <Tooltip title={title} placement="topLeft">
            <span style={{ display: 'inline-block', maxWidth: '230px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>
              {title}
            </span>
          </Tooltip>
        )
      })
    } else {
      cols.push({
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
        width: 250,
        render: (text) => (
          <Tooltip title={text} placement="topLeft">
            <span style={{ display: 'inline-block', maxWidth: '230px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>
              {text}
            </span>
          </Tooltip>
        )
      })
    }

    // 3. Service Metric (Duration/Depth/Pages)
    if (selectedType === 'podcast' || selectedType === 'ai_video') {
      cols.push({
        title: 'Duration (s)',
        dataIndex: 'duration',
        key: 'duration',
        width: 120,
        render: (val) => Number(val).toFixed(2)
      })
    } else if (selectedType === 'maze') {
      cols.push({
        title: 'Depth',
        dataIndex: 'depth',
        key: 'depth',
        width: 100,
      })
    } else if (selectedType === 'course') {
      cols.push({
        title: 'Total Pages',
        dataIndex: 'totalPages',
        key: 'totalPages',
        width: 120,
      })
    }

    /*
    // 4. Organization
    cols.push({
      title: 'Organization',
      dataIndex: 'organization',
      key: 'organization',
      width: 150,
    })
    */
    cols.push({
      title: 'Total Credits',
      dataIndex: 'totalCredits',
      key: 'totalCredits',
      width: 120,
      render: (val) => {
        const num = Number(val)
        return num > 0 && num < 1
          ? num.toLocaleString(undefined, { maximumFractionDigits: 6 })
          : num.toLocaleString()
      }
    })
    const hideTotalCostTypes = ['ai_image', 'ai_video', 'sharebot', 'audio', 'podcast', 'maze']
    if (!hideTotalCostTypes.includes(selectedType)) {
      cols.push({
        title: 'Total Cost',
        dataIndex: 'totalCost',
        key: 'totalCost',
        width: 120,
        render: (val) => {
          const num = Number(val)
          return num > 0 && num < 1
            ? num.toLocaleString(undefined, { maximumFractionDigits: 6 })
            : num.toLocaleString()
        }
      })
    }
    cols.push({
      title: 'Usage Date',
      key: 'timestamp',
      width: 180,
      render: (record) => {
        const timestamp = record?.timestamp && record.timestamp !== '-' ? record.timestamp : record?.created_at
        return timestamp && timestamp !== '-' ? moment(timestamp).format('YYYY-MM-DD HH:mm:ss') : '-'
      }
    })
    return cols
  }, [selectedType])

  const handleExport = () => {
    if (onExportClick) {
      onExportClick(tableData)
    } else {
      if (!tableData || tableData.length === 0) return

      const header = columns.map(c => c.title)
      const rows = tableData.map((row) => {
        return columns.map(c => {
          const val = row[c.dataIndex || c.key]
          if ((c.dataIndex === 'timestamp' || c.key === 'timestamp') && val && val !== '-') {
            return moment(val).format('YYYY-MM-DD HH:mm:ss')
          }
          if ((c.dataIndex === 'model' || c.key === 'model') && val && val !== '-') {
            return formatModelName(val)
          }
          return val
        })
      })

      const csvContent = [header, ...rows]
        .map(e => e.map(v => `"${String(v !== undefined && v !== null ? v : '').replace(/"/g, '""')}"`).join(','))
        .join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const fileName = `credit_usage_${moment().format('YYYY-MM-DD')}.csv`

      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  return (
    <Card className="course-credit-usage-card" style={{ marginTop: '24px' }}>
      <div className="course-credit-usage-content">
        <div className="section-header" style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="section-title" style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{title}</h3>
          {activeTypes.length > 0 && (
            <div className="type-filter-container">
              <span className="type-label">Type: </span>
              <Select
                value={selectedType}
                onChange={onTypeChange}
                className="type-select"
                placeholder="All Services"
                style={{ width: 150 }}
              >
                {activeTypes.map(type => {
                  const colorMap = {
                    'ai-image': '#5B6EE2',
                    'ai_video': '#C9D3FA',
                    audio: '#3875F6',
                    course: '#44959A',
                    podcast: '#EB3B88',
                    maze: '#9FD4FB',
                    sharebot: '#2DCCB9'
                  }

                  let displayName = type.charAt(0).toUpperCase() + type.slice(1)
                  if (type === 'ai-image') displayName = 'AI-Image'
                  else if (type === 'ai_video') displayName = 'Ai-Video'

                  return (
                    <Option key={type} value={type}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colorMap[type] || '#888', display: 'inline-block' }} />
                        {displayName}
                      </div>
                    </Option>
                  )
                })}
              </Select>
            </div>
          )}
        </div>

        <div className="filters-section" style={{ marginBottom: '16px' }}>
          <div className="header-actions" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
            <Button
              onClick={handleExport}
              disabled={tableData.length === 0}
            >
              <DownloadOutlined /> Export Report
            </Button>
            <Search
              placeholder="Search by title, name..."
              allowClear
              value={searchTerm}
              onChange={e => onSearch && onSearch(e.target.value)}
              onSearch={value => onSearch && onSearch(value)}
              style={{ width: 250 }}
            />
          </div>
        </div>

        <div className="table-section">
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={{
                ...pagination,
                onChange: onPageChange,
                onShowSizeChange: onPageChange,
                showSizeChanger: false,
                pageSizeOptions: [10, 20, 50, 100]
              }}
              className="course-credit-table"
              scroll={{ x: 'max-content' }}
            />
          </Spin>
        </div>
      </div>
    </Card>
  )
})

export default CreditUsageTable
